// src/pages/Private/Student/StudentCourses.js
import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchStudentCourses } from '../../../services/studentService';
import { Link } from 'react-router-dom';
import './StudentCourses.css';

function StudentCourses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchStudentCourses();
      setCourses(data);
    } catch (error) {
      setError('Error loading courses: ' + (error.response?.data?.msg || error.message));
      console.error('Kurslar yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getCourseProgress = (course) => {
    // Actual progress will come from backend
    return Math.floor(Math.random() * 100);
  };

  if (loading) {
    return (
      <div className="student-courses-loading">
        <div className="loading-spinner">📚</div>
        <p>Loading courses...</p>
      </div>
    );
  }

  return (
    <div className="student-courses">
      {/* Header */}
      <div className="courses-header">
        <div className="header-content">
          <h1>📚 My Courses</h1>
          <p>All courses you are enrolled in</p>
        </div>
        <button onClick={loadCourses} className="refresh-btn" disabled={loading}>
          <span className="refresh-icon">🔄</span>
          Refresh
        </button>
      </div>

      {error && (
        <div className="courses-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* Course List */}
      {courses.length === 0 ? (
        <div className="empty-courses">
          <div className="empty-icon">📚</div>
          <h3>You are not enrolled in any courses yet</h3>
          <p>Enroll in courses to start learning</p>
        </div>
      ) : (
        <div className="courses-grid">
          {courses.map(course => {
            const progress = getCourseProgress(course);
            
            return (
              <div key={course._id} className="course-card">
                <div className="course-header">
                  <div className="course-image">
                    {course.imageUrl ? (
                      <img src={course.imageUrl} alt={course.title} />
                    ) : (
                      <div className="course-image-placeholder">📚</div>
                    )}
                  </div>
                  <div className="course-basic-info">
                    <h3>{course.title}</h3>
                    <p className="course-category">{course.category || 'General'}</p>
                    <p className="course-teacher">
                      👨‍🏫 {course.teacher?.name || 'Instructor'}
                    </p>
                  </div>
                </div>

                <div className="course-content">
                  <p className="course-description">{course.description}</p>
                  
                  {/* Progress Bar */}
                  <div className="course-progress">
                    <div className="progress-header">
                      <span>Progress</span>
                      <span>%{progress}</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="course-meta">
                    <div className="meta-item">
                      <span className="meta-icon">👥</span>
                      <span>{course.students?.length || 0} students</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-icon">📅</span>
                      <span>{formatDate(course.createdAt)}</span>
                    </div>
                  </div>

                  <div className="course-actions">
                    <Link 
                      to={`/student/course/${course._id}`}
                      className="join-btn"
                    >
                      🎥 Go to Course
                    </Link>
                    <button 
                      className="details-btn"
                      onClick={() => setSelectedCourse(
                        selectedCourse?._id === course._id ? null : course
                      )}
                    >
                      {selectedCourse?._id === course._id ? '▲ Hide Details' : '▼ Show Details'}
                    </button>
                  </div>

                  {selectedCourse?._id === course._id && (
                    <div className="course-details">
                      <div className="detail-section">
                        <h4>About the Course</h4>
                        <p>{course.description}</p>
                      </div>
                      
                      <div className="detail-section">
                        <h4>Course Information</h4>
                        <div className="info-grid">
                          <div className="info-item">
                            <span className="info-label">Category:</span>
                            <span>{course.category || 'General'}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Instructor:</span>
                            <span>{course.teacher?.name || 'Not specified'}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Total Students:</span>
                            <span>{course.students?.length || 0}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Start Date:</span>
                            <span>{formatDate(course.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StudentCourses;